import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Clock,
  ShieldCheck,
  Users,
  CheckCircle2,
  MapPin,
  Plane,
} from "lucide-react";
import Seo from "@/components/Seo";
import { SERVICE_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Corporate Travel", path: "/corporate-travel" },
];

const PILLARS = [
  {
    icon: Briefcase,
    title: "Dedicated Account Managers",
    copy:
      "One named contact, end-to-end ownership from briefing to reconciliation. Senior-level relationship management for corporates that expect predictable, consultative service.",
  },
  {
    icon: Clock,
    title: "48-Hour Proposal Turnaround",
    copy:
      "Submit a brief today, receive a fully-costed proposal in two business days — covering flights, accommodation, transfers, F&B, and creative programming.",
  },
  {
    icon: ShieldCheck,
    title: "24/7 On-Ground Support",
    copy:
      "Local operations teams in every destination. Your delegates are never more than a call away from real human support — visa issues, flight changes, medical needs, anything.",
  },
  {
    icon: Users,
    title: "Group Booking Advantages",
    copy:
      "Long-standing rate agreements with major hotel chains and airlines. Volume-discount access, room-block guarantees, and the kind of upgrades that don't show up in a single booking.",
  },
];

const INCLUSIONS = [
  "Travel policy compliance and reconciliation",
  "Hotel contracting and rate negotiation",
  "Airport transfers and VIP meet-and-greet",
  "F&B and venue coordination",
  "Team-building and cultural experiences",
  "Risk management and duty-of-care protocols",
  "Post-event reconciliation and reporting",
];

const FAQS = [
  {
    q: "What does Trawelldmc handle that an in-house travel desk doesn't?",
    a: "Most corporate travel desks handle individual bookings well, but struggle with the complexity of group programmes — synchronised flight blocks, multi-property hotel contracting, on-ground transfers for 100+ people, F&B coordination, creative programming, and 24/7 operations support. Trawelldmc owns all of this under one contract. Your travel desk continues to handle individual business travel; we handle the group programmes that need a specialised team.",
  },
  {
    q: "How does Trawelldmc manage travel policy compliance?",
    a: "We work with your travel policy as the operating constraint. Class of service, hotel category, per-diem ceilings, advance-booking windows — all built into the proposal from day one. Every booking is logged against your reference codes for post-event reconciliation, and we provide a single line-item invoice rather than dozens of supplier receipts. Finance teams love it.",
  },
  {
    q: "Which destinations does Trawelldmc cover for corporate groups?",
    a: "We operate corporate group programmes across seven destinations — Dubai (our HQ), Singapore, Malaysia, Thailand, Hong Kong, Vietnam and the Philippines. Multi-city programmes spanning two or three of these destinations are a regular part of our work. For destinations outside our core seven, we coordinate through vetted in-region partners under our accountability.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function CorporateTravel() {
  return (
    <div data-testid="corporate-travel-page">
      <Seo
        path="/corporate-travel"
        title="Corporate Group Travel — Dubai & Asia | Trawelldmc"
        description="Trawelldmc manages corporate group travel across Dubai and Asia. Dedicated account managers, 24/7 support, tailored programmes. Trusted by leading companies."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={SERVICE_IMAGES.corporate}
          alt="Trawelldmc corporate group travel management"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">Corporate Travel Management</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.04]">
              Corporate Group <br />Travel <span className="text-[#FF5238]">Management.</span>
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Tailored programmes for boards, leadership teams, and corporate
              groups — from 10 to 1,000+ delegates across Dubai and Southeast
              Asia.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?type=Corporate" className="btn-primary" data-testid="corp-cta-rfp">
                Submit an RFP <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="corp-cta-ai-quote">Instant AI estimate</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">What we deliver</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Predictable. Premium. <br />Accountable.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Corporate group travel is fundamentally different from
              individual business trips. The variables multiply — 50
              passports, 50 dietary preferences, 50 hotel reservations, 50
              ground transfers, all of which need to synchronise across a
              tight schedule with zero margin for error. The brief needs to
              fit your travel policy. Reconciliation needs to fit your
              finance team's reporting cycle. And the experience needs to
              reflect your brand from airport to airport.
            </p>
            <p>
              Trawelldmc has spent two decades operating corporate group
              travel. Our model is built around three commitments — a named
              senior account manager who owns your programme; a 48-hour
              proposal turnaround on most briefs; and 24/7 on-ground support
              by our local operations teams in every destination. Combined
              with long-standing rate agreements at major hotel chains and
              airlines, we deliver the kind of group programmes that look
              effortless to your delegates and audit-ready for your finance
              team.
            </p>
            <p>
              From a 12-pax board offsite in Dubai to a 500-pax sales
              kick-off across Singapore and Bali, the same playbook scales
              appropriately. Brief, propose, deliver, reconcile — owned
              end-to-end by one accountable team.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Why corporates choose us</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              The Trawelldmc operating model.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="card-flat p-8">
                <p.icon className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{p.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{p.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline !text-[#FF5238] mb-4">Scope of work</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              What we own on <br /><span className="text-[#FF5238]">every brief.</span>
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-5">
            {INCLUSIONS.map((i) => (
              <li key={i} className="flex gap-4 items-start border-t border-white/15 pt-5">
                <CheckCircle2 className="h-6 w-6 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-lg text-white/90">{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`corp-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Plan your next <br /><span className="text-[#FF5238]">corporate programme.</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Share your headcount, destination and dates. Receive a costed
              proposal within 48 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?type=Corporate" className="btn-primary w-full justify-center" data-testid="corp-bottom-cta">
              Submit your brief <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our team</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">Explore related</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/mice-services" className="group flex items-start gap-3">
              <Briefcase className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">MICE Services</div>
                <div className="text-sm text-slate-500 mt-1">Meetings, conferences, exhibitions</div>
              </div>
            </Link>
            <Link to="/incentive-travel" className="group flex items-start gap-3">
              <Briefcase className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Incentive Travel</div>
                <div className="text-sm text-slate-500 mt-1">Reward top performers</div>
              </div>
            </Link>
            <Link to="/dubai-dmc" className="group flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
